import styled from "@emotion/styled";
import PropTypes from "prop-types";
import { graphql, navigate, useStaticQuery } from "gatsby";

import FriendlyCaptcha from "~/components/FriendlyCaptcha";

import React, {
    isValidElement,
    createElement,
    useEffect,
    useMemo,
    useState,
    useRef,
} from "react";

import {
    find,
    has,
    includes,
    isEmpty,
    isFunction,
    isObject,
    map,
    pick,
    reduce,
    some,
} from "lodash";

import TextButton from "~/components/buttons/TextButton";
import StandardButton from "~/components/buttons/Button";
import TextLink from "~/components/links/TextLink";
import ButtonLink from "~/components/links/ButtonLink";

import BaseModal from "~/components/BaseModal";
import VideoEmbed from "~/components/VideoEmbed";
import TermsOfUse from "~/components/TermsOfUse";
import ErrorBoundary from "~/components/ErrorBoundary";
import Form from "~/queryBlocks/Form";

import { useMarketingContext } from "~/context/MarketingContext";
import { useLocationContext } from "~/context/LocationContext";
import usePardotTracking from "~/hooks/marketing/usePardotTracking";

import {
    checkDocumentAttributes,
    getFileUrl,
    getProductLines,
    trackDownload,
    automaticallyDownloadFile,
    getDocumentTermsOfUse,
    getDocumentToProductSlug,
    getFormIdByProductLine,
} from "~/utilities/helpers";

import {
    getLocalStorageSerialize,
    setLocalStorageSerialize,
} from "~/utilities/localStorageHelpers";

import {
    baseTextDecorationCss,
    hoverTextDecorationCss,
} from "~/styles/helpers";

import { formatDownloadVideo } from "~/utilities/videoHelpers";

const SubmitButton = styled(TextButton)`
    text-align: left;
    display: inline-flex;
    align-items: center;
    ${baseTextDecorationCss};

    &:hover,
    &:focus {
        ${hoverTextDecorationCss};
    }
`;

const StandardSubmitButton = styled(StandardButton)`
    text-align: left;
    display: inline-flex;
    align-items: center;
`;

const DownloadInlineButton = styled(TextLink)`
    cursor: pointer;
    text-align: left;
`;

const DownloadButton = styled(ButtonLink)`
    cursor: pointer;
    text-align: left;
`;

const InlineWrapper = styled.span`
    display: inline-flex;
    align-items: flex-start;
`;

const shouldDownload = (doc) => {
    return (
        has(doc, `documentsAttributes.downloadFileOnFormSubmission`) &&
        doc.documentsAttributes.downloadFileOnFormSubmission
    );
};

const getDocumentToProductSku = (doc, isCad) => {
    const isDownloadDocument = has(doc, "documentsAttributes");
    const documentToProduct = doc?.documentsAttributes?.documentToProduct || [];
    const articleToProducts = doc?.details?.articleToProducts || [];
    const cadModelsToProduct =
        doc?.documentsAttributes?.cadModelsToProduct || [];

    let documentToProducts = isDownloadDocument
        ? documentToProduct
        : articleToProducts;

    if (isCad && isDownloadDocument && !isEmpty(cadModelsToProduct)) {
        documentToProducts = cadModelsToProduct;
    }

    const hasAssociatedProducts = !isEmpty(documentToProducts);

    return hasAssociatedProducts
        ? reduce(
              documentToProducts.nodes,
              (skus, product) => {
                  return skus.concat(product.globalAttributes.sku);
              },
              [],
          )
        : [];
};

const cleanDocForSerialization = (doc) => {
    return pick(doc, [
        "id",
        "title",
        "url",
        "slug",
        "details.urlOverride",
        "documentsAttributes.downloadFileOnFormSubmission",
    ]);
};

const getGatedStorageKey = (doc) => {
    const documentToProductSlug = getDocumentToProductSlug(doc);
    return `document-${documentToProductSlug}`;
};

export const setGatedLocalStorage = (doc) => {
    const STORAGE_KEY = getGatedStorageKey(doc);
    const gatedFormStorage = getLocalStorageSerialize("gatedFormStorage", []);
    gatedFormStorage.push(STORAGE_KEY);
    setLocalStorageSerialize("gatedFormStorage", gatedFormStorage);
};

export const isGatedDocument = (doc) => {
    const STORAGE_KEY = getGatedStorageKey(doc);
    const gatedFormStorage = getLocalStorageSerialize("gatedFormStorage", []);
    const isGated = checkDocumentAttributes(doc, `isGated`);
    const formWasNotCompleted = !gatedFormStorage.includes(STORAGE_KEY);
    return isGated && formWasNotCompleted;
};

const setTermsOfUseLocalStorage = (doc) => {
    const termsOfUseObject = getDocumentTermsOfUse(doc);
    const termsStorage = getLocalStorageSerialize("termsOfUseStorage", []);
    termsStorage.push(termsOfUseObject.documentStorage);
    setLocalStorageSerialize("termsOfUseStorage", termsStorage);
};

export const isGatedTermsOfUse = (doc, isGated) => {
    const termsOfUseObject = getDocumentTermsOfUse(doc);
    const shouldUseTermsOfUse = !isGated && !!termsOfUseObject.terms;

    if (!shouldUseTermsOfUse) {
        return false;
    }

    const termsStorage = getLocalStorageSerialize("termsOfUseStorage", []);

    return !termsStorage.includes(termsOfUseObject.documentStorage);
};

const getFormId = (doc, formsOptionsPage) => {
    const formInfo =
        formsOptionsPage?.gatedDocumentForms?.gatedDocumentForms || [];
    const productLines = getProductLines(doc?.productLines ?? []);
    let formId = checkDocumentAttributes(doc, `overrideFormId`);

    if (!formId && productLines.length) {
        formId = getFormIdByProductLine(formInfo, productLines[0]);
    }

    return formId;
};

const getProductInfo = (doc, hiddenFieldValues) => {
    const documentToProduct =
        doc?.documentsAttributes?.documentToProduct?.nodes || [];

    const product = documentToProduct[0];

    const productUrl = product?.productUrl || "";

    const productSku = product?.globalAttributes?.sku || "";

    const productCategory = find(
        product?.productCategories?.nodes,
        (category) => {
            return some(category?.ancestors?.nodes, (ancestor) => !!ancestor);
        },
    );

    const subcategory = productCategory?.name ?? "";

    return {
        product_url: hiddenFieldValues.product_url || productUrl,
        sku: hiddenFieldValues.product_sku || productSku,
        subcategory: hiddenFieldValues.subcategory || subcategory,
    };
};

const GatedButton = ({
    doc,
    color,
    lang,
    children,
    replacementComponent,
    replacementComponentProps,
    openInSameTab,
    eventDataOverride,
    onSuccessfulSubmit,
    onClick,
    handleTracking,
    clearParentState,
    setIsGated,
    inline,
}) => {
    const {
        wp: { formsOptionsPage },
    } = useStaticQuery(graphql`
        query StaticGatedButtonQuery {
            wp {
                formsOptionsPage {
                    gatedDocumentForms {
                        gatedDocumentForms {
                            formId
                            productLineSlug
                        }
                    }
                }
            }
        }
    `);

    const [openModal, setOpenModal] = useState(false);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const newTabRef = useRef(null);

    const [isGatedLocalState, setIsGatedLocalState] = useState(true);

    const { hiddenFieldValues } = useMarketingContext();

    const ButtonComponent = inline ? SubmitButton : StandardSubmitButton;

    const Button = replacementComponent ?? ButtonComponent;

    const formId = getFormId(doc, formsOptionsPage);

    const runOnSubmitActions = {
        saveToPardotStorage: {
            doc: cleanDocForSerialization(doc),
            value: null,
        },
        downloadFile: {
            doc: cleanDocForSerialization(doc),
            isGated: true,
            openInSameTab,
        },
        tracking: {
            event: "gaEvent",
            values: eventDataOverride,
        },
    };

    const runOnSubmit = () => {
        if (isFunction(onSuccessfulSubmit)) {
            onSuccessfulSubmit();
        }

        if (shouldDownload(doc)) {
            newTabRef.current = window.open("/preparing-download", "_blank");
            setIsGatedLocalState(false);
            setGatedLocalStorage(doc);

            handleTracking();

            setShowCaptcha(true);
        }
    };

    const onCaptchaComplete = (token) => {
        automaticallyDownloadFile(
            doc,
            openInSameTab,
            false,
            null,
            token,
            newTabRef.current,
        );
        setShowCaptcha(false);
    };

    const onRequestClose = () => {
        if (isFunction(clearParentState)) {
            clearParentState();
        }

        setIsGated(isGatedLocalState);
        setOpenModal(false);
    };

    const { subcategory, product_url, sku } = getProductInfo(
        doc,
        hiddenFieldValues,
    );

    return (
        <>
            <Form
                closeModalProp={onRequestClose}
                formPresetValues={{
                    fileId: doc.id,
                    fileName: doc.title,
                    reference_design: doc.title,
                    subcategory,
                    product_url,
                    sku,
                }}
                modalForm={true}
                isModalOpenProp={openModal}
                useChildButton={true}
                {...{
                    runOnSubmitActions,
                    runOnSubmit,
                    formId,
                }}
            >
                <Button
                    type="button"
                    color={color}
                    lang={lang}
                    disabled={showCaptcha}
                    onClick={(event) => {
                        if (isFunction(onClick)) {
                            onClick(event);
                        }

                        setOpenModal(true);
                    }}
                    {...replacementComponentProps}
                >
                    {children}
                </Button>
            </Form>
            {showCaptcha ? (
                <FriendlyCaptcha
                    onComplete={onCaptchaComplete}
                    startMode="auto"
                />
            ) : null}
        </>
    );
};

const TermsAndConditionsButton = ({
    color,
    lang,
    doc,
    replacementComponent,
    replacementComponentProps,
    openInSameTab,
    handleTracking,
    onClick,
    onSuccessfulSubmit,
    clearParentState,
    inline,
    children,
}) => {
    const [openModal, setOpenModal] = useState(false);

    const { location } = useLocationContext();

    const ButtonComponent = inline ? SubmitButton : StandardSubmitButton;

    const Button = replacementComponent ?? ButtonComponent;

    const termsOfUseObject = getDocumentTermsOfUse(doc);

    const setDocTermsStorage = () => {
        const params = new URLSearchParams(location.search);

        params.append("termsOfUse", "true");

        setTermsOfUseLocalStorage(doc);

        navigate(`?${params.toString()}`);
    };

    const startDownload = (token, newTab) => {
        if (isFunction(onSuccessfulSubmit)) {
            onSuccessfulSubmit();
        }

        handleTracking();

        automaticallyDownloadFile(
            doc,
            openInSameTab,
            false,
            null,
            token,
            newTab,
        );
    };

    const onRequestClose = () => {
        if (isFunction(clearParentState)) {
            clearParentState();
        }

        setOpenModal(false);
    };

    return (
        <>
            <Button
                onClick={(event) => {
                    if (isFunction(onClick)) {
                        onClick(event);
                    }

                    setOpenModal(true);
                }}
                color={color}
                lang={lang}
                {...replacementComponentProps}
            >
                {children}
            </Button>
            <BaseModal isOpen={openModal} onRequestClose={onRequestClose}>
                <TermsOfUse
                    closeModal={() => setOpenModal(false)}
                    {...{ setDocTermsStorage, startDownload }}
                    terms={termsOfUseObject.terms}
                />
            </BaseModal>
        </>
    );
};

const DocumentButton = ({
    color,
    lang,
    doc,
    replacementComponent,
    replacementComponentProps,
    handleTracking,
    onClick,
    clearParentState,
    inline,
    openInSameTab,
    children,
}) => {
    const ButtonComponent = inline ? DownloadInlineButton : DownloadButton;
    const [showCaptcha, setShowCaptcha] = useState(false);
    const newTab = useRef(null);

    const LinkButton = replacementComponent ?? ButtonComponent;

    // Use the direct file URL as the href so right-click copy/open-in-new-tab bypasses the captcha
    const isDownload = includes(doc?.latestDlmVersion, "/uploads/");

    const linkTo = getFileUrl(doc);

    const onCaptchaComplete = (token) => {
        try {
            automaticallyDownloadFile(
                doc,
                openInSameTab,
                false,
                null,
                token,
                newTab.current,
            );
            setShowCaptcha(false);
        } catch (e) {
            console.error(`Error getting recaptcha response: ${e.message}`);
        }
    };

    return (
        <>
            <LinkButton
                showTextDecoration={inline}
                targetElement="span"
                to={linkTo}
                target={openInSameTab ? "_self" : "_blank"}
                onClick={(event) => {
                    handleTracking();

                    if (!openInSameTab && isDownload) {
                        newTab.current = window.open(
                            "/preparing-download",
                            "_blank",
                        );
                    }

                    if (isFunction(onClick)) {
                        onClick(event);
                    }

                    if (isFunction(clearParentState)) {
                        clearParentState();
                    }

                    if (isDownload) {
                        setShowCaptcha(true);
                        event.preventDefault();
                    }
                }}
                color={color}
                lang={lang}
                disabled={showCaptcha}
                {...replacementComponentProps}
            >
                <InlineWrapper>{children}</InlineWrapper>
            </LinkButton>
            {showCaptcha ? (
                <FriendlyCaptcha
                    onComplete={onCaptchaComplete}
                    startMode="auto"
                />
            ) : null}
        </>
    );
};

const VideoButton = ({
    replacementComponent,
    replacementComponentProps,
    handleTracking,
    onClick,
    color,
    lang,
    doc,
    clearParentState,
    inline,
    videoUrl,
    isEmbed,
    poster,
    children,
}) => {
    const [openModal, setOpenModal] = useState(false);

    const ButtonComponent = inline ? SubmitButton : StandardSubmitButton;

    const Button = replacementComponent ?? ButtonComponent;

    const onRequestClose = () => {
        if (isFunction(clearParentState)) {
            clearParentState();
        }

        setOpenModal(false);
    };

    return (
        <>
            <Button
                type="button"
                onClick={(event) => {
                    if (isFunction(onClick)) {
                        onClick(event);
                    }

                    handleTracking();

                    setOpenModal(true);
                }}
                color={color}
                lang={lang}
                {...replacementComponentProps}
            >
                {children}
            </Button>
            <BaseModal isOpen={openModal} onRequestClose={onRequestClose}>
                <VideoEmbed
                    url={videoUrl}
                    title={doc.title}
                    isEmbed={isEmbed}
                    poster={poster}
                />
            </BaseModal>
        </>
    );
};

const GatedDocumentButton = ({
    doc,
    children,
    lang,
    isCad = false,
    onClick = () => {},
    onSuccessfulSubmit = () => {},
    sku = "",
    replacementComponent = null,
    replacementComponentProps = {},
    clearParentState = null,
    color = "",
    inline = false,
}) => {
    const [isGated, setIsGated] = useState(false);

    const [isGatedTerms, setIsGatedTerms] = useState(false);

    const { onDownloadSubmission } = usePardotTracking();

    useEffect(() => {
        const isGated = isGatedDocument(doc);
        const isGatedTerms = isGatedTermsOfUse(doc, isGated);
        setIsGated(isGated);
        setIsGatedTerms(isGatedTerms);
    }, [doc]);

    const openInSameTab = checkDocumentAttributes(doc, `openInSameTab`);

    const eventDataOverride = useMemo(() => {
        const documentCategories = doc?.documentCategories?.nodes;
        const category =
            map(documentCategories, ({ name }) => name).join(", ") || "";

        const productSku = getDocumentToProductSku(doc, isCad).join(", ");
        const docSku = !isEmpty(sku) ? sku : productSku;

        let productLine = "notSet";

        const productLines = getProductLines(doc?.productLines);

        if (productLines.length) {
            productLine = productLines[0]?.name;
        }

        const data = {
            label: doc.title,
            category: category,
            additionalTracking: {
                productLine: productLine,
                documentIsCad: isCad,
                documentIsGated: isGated,
            },
        };

        if (docSku) {
            data.additionalTracking.eventSku = docSku;
        }

        return data;
    }, [doc, isCad, isGated, sku]);

    const handleTracking = () => {
        trackDownload("gaEvent", eventDataOverride);
        onDownloadSubmission(doc);
    };

    const isVideo = checkDocumentAttributes(doc, `isVideo`);
    const videoAttrs = formatDownloadVideo(doc);
    const isEmbed = videoAttrs?.isEmbed;
    const poster = videoAttrs?.poster;
    const videoUrl = videoAttrs?.url;

    const sharedProps = {
        doc,
        clearParentState,
        color,
        lang,
        inline,
        replacementComponent:
            replacementComponent &&
            isValidElement(createElement(replacementComponent))
                ? replacementComponent
                : null,
        replacementComponentProps: isObject(replacementComponentProps)
            ? replacementComponentProps
            : {},
    };

    return (
        <ErrorBoundary>
            {isGated ? (
                <GatedButton
                    {...sharedProps}
                    {...{
                        onClick,
                        children,
                        openInSameTab,
                        eventDataOverride,
                        onSuccessfulSubmit,
                        handleTracking,
                        setIsGated: (isGated) => {
                            setIsGated(isGated);
                        },
                    }}
                >
                    {children}
                </GatedButton>
            ) : isGatedTerms ? (
                <TermsAndConditionsButton
                    {...sharedProps}
                    {...{
                        openInSameTab,
                        handleTracking,
                        onClick,
                        onSuccessfulSubmit,
                    }}
                >
                    {children}
                </TermsAndConditionsButton>
            ) : isVideo && videoUrl ? (
                <VideoButton
                    {...sharedProps}
                    {...{
                        handleTracking,
                        videoUrl,
                        isEmbed,
                        poster,
                    }}
                >
                    {children}
                </VideoButton>
            ) : (
                <DocumentButton
                    {...sharedProps}
                    {...{
                        handleTracking,
                        onClick,
                        openInSameTab,
                    }}
                >
                    {children}
                </DocumentButton>
            )}
        </ErrorBoundary>
    );
};

GatedDocumentButton.propTypes = {
    doc: PropTypes.shape({
        title: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
    }).isRequired,
    children: PropTypes.node.isRequired,
    inline: PropTypes.bool,
    onClick: PropTypes.func,
    onSuccessfulSubmit: PropTypes.func,
    table: PropTypes.bool,
    isCad: PropTypes.bool,
    sku: PropTypes.string,
    replacementComponent: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.element,
        PropTypes.elementType,
    ]),
    replacementComponentProps: PropTypes.shape({}),
    clearParentState: PropTypes.func,
    color: PropTypes.string,
    buttonText: PropTypes.string,
};

export default GatedDocumentButton;
